<template>
  <div>
    <v-card outlined class="pa-4 mb-4">
      <div class="d-flex align-start flex-wrap mb-3" style="gap: 12px;">
        <div>
          <div class="pw-section">{{ title }}</div>
          <div class="caption grey--text text--darken-1">{{ subtitle }}</div>
        </div>
        <v-spacer />
        <v-btn-toggle v-model="view" mandatory dense color="primary">
          <v-btn value="month" small>Mes</v-btn>
          <v-btn value="year" small>Año</v-btn>
        </v-btn-toggle>
      </div>

      <div class="d-flex align-center flex-wrap mb-3" style="gap: 12px;">
        <v-select v-model="year" :items="years" label="Año" outlined dense hide-details class="period-filter" />
        <v-select
          v-if="view === 'month'"
          v-model="month"
          :items="monthOptions"
          label="Mes"
          outlined
          dense
          hide-details
          class="period-filter"
        />
        <v-select v-model="line" :items="lineOptions" label="Línea de negocio" outlined dense hide-details class="period-filter" />
        <v-switch v-model="bciOnly" label="Solo BCI" inset dense hide-details class="mt-0" />
      </div>

      <div v-if="loading" class="d-flex justify-center py-10">
        <v-progress-circular indeterminate color="primary" />
      </div>
      <v-alert v-else-if="error" type="error" dense outlined>{{ error }}</v-alert>
      <div v-else-if="!points.length" class="text-center grey--text py-10">Sin datos para este período</div>
      <template v-else>
        <!-- vue-chartjs mete su propio contenedor y, sin `styles`, el canvas usa su alto por
             defecto (400px) y se sale de la tarjeta. -->
        <area-chart :chart-data="chartData" :options="chartOptions" :styles="chartStyles" />
        <p class="caption grey--text text--darken-1 mt-3 mb-0">
          <template v-if="clickable">Haz clic en {{ view === 'month' ? 'un día' : 'un mes' }} para ver sus altas y bajas.</template>
          No cuentan como alta ni baja las bajas revertidas en menos de 24 horas ni los cambios de plan.
          <span v-if="hasApproximate">* Meses aproximados: el historial de estados está completo solo desde julio de 2026.</span>
          <span v-if="isMrr">El MRR usa los precios y el tipo de cambio de hoy: muestra cambios de cartera, no de precio.</span>
        </p>
      </template>
    </v-card>

    <B2CV2BarDetail
      v-if="detail.selection"
      :selection="detail.selection"
      :rows="detail.rows"
      :loading="detail.loading"
      :error="detail.error"
      class="mb-4"
      @close="detail = emptyDetail()"
    />
  </div>
</template>

<script>
import { Line } from 'vue-chartjs';
import { TREND_COLORS } from '@/components/B2CTrendChart.vue';
import B2CV2BarDetail from '@/components/b2c-report-v2/B2CV2BarDetail.vue';
import B2CReportV2Service from '@/services/b2c-report-v2.service';
import { formatNumber } from '@/utils/b2c-report.utils';

// La vista Mes parte en julio de 2026: antes el historial de estados no es confiable día a día.
const FIRST_DAILY_MONTH = '2026-07';
// El reporte parte en 2026.
const FIRST_YEAR = 2026;

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

// Las tres líneas que, apiladas, suman el total B2C/B2B2C. Loyalty no entra.
export const STACK_LINES = [
  { key: 'b2c', label: 'B2C', color: TREND_COLORS.primary },
  { key: 'b2b2c', label: 'B2B2C', color: TREND_COLORS.muted },
  { key: 'upsell', label: 'Upsell', color: TREND_COLORS.faint }
];

// Relleno translúcido de cada área, para que se lea la línea de borde encima.
const AREA_ALPHA = 0.35;

const hexToRgba = (hex, alpha) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const AreaChart = {
  extends: Line,
  props: ['chartData', 'options'],
  mounted() {
    this.renderChart(this.chartData, this.options);
  },
  watch: {
    chartData: {
      deep: true,
      handler(newData) {
        if (this.$data._chart) this.$data._chart.destroy();
        this.renderChart(newData, this.options);
      }
    }
  }
};

const emptyDetail = () => ({ selection: null, rows: [], loading: false, error: '' });

export default {
  name: 'B2CV2TrendPanel',
  components: { AreaChart, B2CV2BarDetail },
  props: {
    // 'mrr': MRR en USD; 'count': membresías activas.
    metric: { type: String, default: 'mrr' },
    country: { type: String, default: null },
    // Hoy en Chile, 'YYYY-MM-DD'.
    today: { type: String, required: true },
    // Clic en un día o mes para ver sus membresías (requiere `mrr-b2c-b2b2c:view-memberships`).
    clickable: { type: Boolean, default: false }
  },
  data() {
    return {
      // Por defecto el año completo y solo BCI (Guille, 2026-11-03).
      view: 'year',
      year: Number(this.today.slice(0, 4)),
      month: Number(this.today.slice(5, 7)),
      line: 'all',
      bciOnly: true,
      // Vista Mes: los días de /daily. Vista Año: los meses de /trend. Los dos traen `series`.
      points: [],
      loading: false,
      error: '',
      detail: emptyDetail()
    };
  },
  computed: {
    isMrr() {
      return this.metric === 'mrr';
    },
    years() {
      const last = Number(this.today.slice(0, 4));
      return Array.from({ length: last - FIRST_YEAR + 1 }, (_, i) => FIRST_YEAR + i);
    },
    /** Meses del año elegido entre julio de 2026 y el mes en curso. */
    monthOptions() {
      const current = this.today.slice(0, 7);
      return MONTH_NAMES.map((text, i) => ({ text, value: i + 1 })).filter((m) => {
        const key = this.monthKey(this.year, m.value);
        return key >= FIRST_DAILY_MONTH && key <= current;
      });
    },
    selectedMonth() {
      return this.monthKey(this.year, this.month);
    },
    lineOptions() {
      return [{ text: 'Todas', value: 'all' }, ...STACK_LINES.map((l) => ({ text: l.label, value: l.key }))];
    },
    shownLines() {
      return this.line === 'all' ? STACK_LINES : STACK_LINES.filter((l) => l.key === this.line);
    },
    title() {
      const what = this.isMrr ? 'MRR' : 'Membresías activas';
      const period = this.view === 'month' ? `${MONTH_NAMES[this.month - 1]} ${this.year}` : String(this.year);
      return `${what} · ${period}${this.bciOnly ? ' · solo BCI' : ''}`;
    },
    subtitle() {
      const what = this.isMrr ? 'MRR en USD de las membresías activas' : 'Membresías activas';
      const when = this.view === 'month' ? 'al cierre de cada día' : 'al cierre de cada mes';
      return `${what} ${when}, apiladas por línea de negocio`;
    },
    hasApproximate() {
      return this.points.some((p) => p.approximate);
    },
    chartStyles() {
      return { position: 'relative', height: '320px', width: '100%' };
    },
    chartData() {
      return {
        labels: this.points.map((p) => this.labelOf(p)),
        // La primera área parte del eje y cada una rellena hasta la anterior: el borde de
        // arriba es el total.
        datasets: this.shownLines.map((l, i) => ({
          label: this.bciOnly ? `${l.label} (BCI)` : l.label,
          data: this.points.map((p) => this.valueOf(this.flowsOf(p, l.key))),
          borderColor: l.color,
          backgroundColor: hexToRgba(l.color, AREA_ALPHA),
          pointBackgroundColor: l.color,
          pointRadius: this.view === 'month' ? 3 : 4,
          pointHoverRadius: 6,
          borderWidth: 2,
          fill: i === 0 ? 'origin' : '-1',
          lineTension: 0.3
        }))
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: this.shownLines.length > 1, position: 'bottom', labels: { usePointStyle: true, padding: 20 } },
        // Por columna: un clic en cualquier altura del día o mes lo elige.
        hover: { mode: 'index', intersect: false },
        onHover: (event, elements) => {
          event.target.style.cursor = this.clickable && elements.length ? 'pointer' : 'default';
        },
        onClick: (event, elements) => this.selectPoint(elements),
        scales: {
          xAxes: [{
            stacked: true,
            gridLines: { display: false },
            scaleLabel: { display: this.view === 'month', labelString: 'Día' }
          }],
          yAxes: [{
            stacked: true,
            ticks: { beginAtZero: true, maxTicksLimit: 6, callback: (v) => this.format(v) },
            gridLines: { color: 'rgba(0,0,0,0.05)' }
          }]
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            title: (items) => this.tooltipTitle(this.points[items[0].index]),
            label: (item) => this.tooltipLine(this.points[item.index], this.shownLines[item.datasetIndex]),
            footer: (items) => `Total: ${this.format(items.reduce((sum, i) => sum + Number(i.value), 0))}`
          }
        }
      };
    }
  },
  watch: {
    view: 'load',
    year() {
      // En vista Mes, el mes elegido puede quedar fuera del año nuevo: se toma el último disponible.
      if (this.view === 'month' && !this.monthOptions.some((m) => m.value === this.month)) {
        this.month = this.monthOptions[this.monthOptions.length - 1].value;
        return;
      }
      this.load();
    },
    month() {
      if (this.view === 'month') this.load();
    },
    country: 'load',
    // El listado abierto queda con el filtro de cuando se abrió; se cierra para no confundir.
    bciOnly() {
      this.detail = emptyDetail();
    },
    line() {
      this.detail = emptyDetail();
    }
  },
  created() {
    this.load();
  },
  methods: {
    emptyDetail,
    monthKey(year, month) {
      return `${year}-${String(month).padStart(2, '0')}`;
    },
    /** Lo que se está viendo; si cambia mientras carga, la respuesta ya no aplica. */
    requestKey() {
      return [this.view, this.view === 'month' ? this.selectedMonth : this.year, this.country].join('|');
    },
    async load() {
      const key = this.requestKey();
      this.detail = emptyDetail();
      this.loading = true;
      this.error = '';
      const result = this.view === 'month'
        ? await B2CReportV2Service.getDaily({ month: this.selectedMonth, country: this.country })
        : await B2CReportV2Service.getTrend({ year: this.year, country: this.country });
      if (key !== this.requestKey()) return;
      this.loading = false;
      if (result.status !== 'ok') {
        this.points = [];
        this.error = result.message;
        return;
      }
      this.points = this.view === 'month' ? result.data.days : result.data.months;
    },
    labelOf(point) {
      if (point.date) return Number(point.date.slice(8, 10));
      const label = MONTH_NAMES[Number(point.month.slice(5, 7)) - 1].slice(0, 3);
      return point.approximate ? `${label}*` : label;
    },
    tooltipTitle(point) {
      if (point.date) {
        const [y, m, d] = point.date.split('-');
        return `${d}/${m}/${y}`;
      }
      const name = `${MONTH_NAMES[Number(point.month.slice(5, 7)) - 1]} ${point.month.slice(0, 4)}`;
      return point.approximate ? `${name} (aproximado)` : name;
    },
    tooltipLine(point, line) {
      const f = this.flowsOf(point, line.key);
      const moves = `altas ${formatNumber(f.activations)} · bajas ${formatNumber(f.churns)}`;
      if (this.isMrr) return `${line.label}: ${this.format(f.mrrUsd)} (${formatNumber(f.activeEnd)} membresías)`;
      return `${line.label}: ${formatNumber(f.activeEnd)} (${moves})`;
    },
    /** Lo que mide el gráfico: MRR en USD o cantidad de membresías activas. */
    valueOf(flows) {
      return this.isMrr ? flows.mrrUsd : flows.activeEnd;
    },
    format(value) {
      return this.isMrr ? `USD ${formatNumber(value)}` : formatNumber(value);
    },
    /** Serie de una línea, o su parte BCI cuando "Solo BCI" está activo. */
    flowsOf(point, lineKey) {
      return point.series[this.bciOnly ? `${lineKey}Bci` : lineKey];
    },
    selectPoint(elements) {
      if (!this.clickable || !elements || !elements.length) return;
      const point = this.points[elements[0]._index];
      if (point) this.openDetail(point);
    },
    /** Lista las altas y bajas del día o mes elegido, con el filtro BCI y la línea del gráfico. */
    async openDetail(point) {
      const period = point.date ? { date: point.date } : { month: point.month };
      const selection = { ...period, bciOnly: this.bciOnly, line: this.line };
      this.detail = { selection, rows: [], loading: true, error: '' };
      const filters = { ...period, bciOnly: this.bciOnly, country: this.country };
      const result = point.date
        ? await B2CReportV2Service.getDailyDetail(filters)
        : await B2CReportV2Service.getTrendDetail(filters);
      // Si eligieron otro punto mientras cargaba, esta respuesta ya no aplica.
      if (this.detail.selection !== selection) return;
      this.detail.loading = false;
      if (result.status !== 'ok') {
        this.detail.error = result.message;
        return;
      }
      this.detail.rows = result.data;
    }
  }
};
</script>

<style scoped>
.period-filter {
  max-width: 180px;
}
</style>
